import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Params } from '@angular/router';
import { QarepaProgressBarService } from '@qarepa/common';
import { OAuthStorageService } from '@qaroni-app/auth/services/o-auth-storage.service';
import { CheckWidgetService } from '@qaroni-app/check/services/check-widget.service';
import { CheckService } from '@qaroni-app/check/services/check.service';
import { Check } from '@qaroni-app/check/types/check';
import { CheckCategoryEnum } from '@qaroni-app/check/types/check-category.enum';
import { Journey } from '@qaroni-app/check/types/journey';
import { EmployeeCheckSourceService } from '@qaroni-app/employee/services/employee-access-configuration/employee-check-source.service';
import { EmployeeCheckSource } from '@qaroni-app/employee/types/employee-check-source';
import { WorkersService } from '@qaroni-app/workers/services/workers.service';
import { UserRole } from '@qaroni-app/workers/types/user-role';
import { CheckWidgetCardDesignConfig } from '@qaroni-core/config/app/check-widget-card-design.config';
import { qaroniAnimations } from '@qaroni-shared/animations/qaroni-animations';
import { DisplayTimeService } from '@qaroni-shared/services/display-time.service';
import { format, parseISO } from 'date-fns';
import { filter, shareReplay } from 'rxjs/operators';
import { CheckWidgetForm } from './check-widget.form';

@Component({
  selector: 'qaroni-check-widget',
  templateUrl: './check-widget.component.html',
  styleUrls: ['./check-widget.component.scss'],
  animations: [qaroniAnimations.enterFadeIn()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckWidgetComponent
  extends CheckWidgetForm
  implements OnInit, OnDestroy
{
  readonly hideDragButton = input<boolean>(false);

  private route = inject(ActivatedRoute);
  private progressBar = inject(QarepaProgressBarService);
  private checkService = inject(CheckService);
  private checkWidgetService = inject(CheckWidgetService);
  private workersService = inject(WorkersService);
  private oAuthStorageService = inject(OAuthStorageService);
  private employeeCheckSourceService = inject(EmployeeCheckSourceService);
  private displayTimeService = inject(DisplayTimeService);
  private _cdr = inject(ChangeDetectorRef);

  public buttonWork = CheckWidgetCardDesignConfig.desactive_work;
  public buttonBreak = null;
  public infoTooltip = $localize`No hay fichajes en la jornada de hoy`;

  public indicatorLabel = this.buttonWork.label;
  public indicatorColor = this.buttonWork.classBgStatus;
  public journey: Journey | null = null;
  public check: Check | null = null;
  private companyID = 0;
  private employeeID = 0;
  public enableCheck = false;
  public allowBreakCheck = false;
  public checkCategory = CheckCategoryEnum;

  private oAuth = this.oAuthStorageService.get();
  public isLoading$ = this.progressBar.getProgressBar$().pipe(shareReplay(1));
  private userRole$ = this.workersService.getUserRole$().pipe(shareReplay(1));
  private journey$ = this.checkWidgetService.getJourney$().pipe(shareReplay(1));
  private check$ = this.checkWidgetService.getCheck$().pipe(shareReplay(1));
  private minutes$ = this.displayTimeService.minute$();
  private checkConfig$ =
    this.employeeCheckSourceService.getChecksSourceSubject$();
  private journeyTable$ = this.checkService.getJourney$().pipe(
    filter(journey => this.journey.dayCheck === journey?.dayCheck),
    shareReplay(1)
  );

  ngOnInit(): void {
    this.subs.add(this.checkConfig$.subscribe(this.getCheckConfig));
    this.subs.add(this.userRole$.subscribe(this.getUserRole));
    this.subs.add(this.route.paramMap.subscribe(this.getParamMap));
    this.subs.add(this.journey$.subscribe(this.getJourney));
    this.subs.add(this.journeyTable$.subscribe(this.getJourneyTable));
    this.subs.add(this.check$.subscribe(this.getCheck));
    this.subs.add(this.minutes$.subscribe(this.getMinutes));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.displayTimeService.stop();
  }

  get tooltipText(): string {
    return this.enableCheck
      ? ''
      : $localize`El administrador no ha habilitado el fichaje web.`;
  }

  private getParamMap = (paramMap: ParamMap): void => {
    if (paramMap.has('companyID') && this.oAuth?.userId) {
      this.companyID = parseInt(paramMap.get('companyID'));
      this.workersService.getUserRole(this.companyID, this.oAuth.userId);
    }
  };

  private getUserRole = (userRole: UserRole): void => {
    if (userRole?.employeeId) {
      this.employeeID = userRole.employeeId;
      const now = new Date().toISOString().slice(0, -1);
      const params: Params = {
        currentDate: format(parseISO(now), 'yyyy-MM-dd HH:mm:ss'),
      };
      this.checkWidgetService.getJourney(this.employeeID, params);
      this.employeeCheckSourceService.getEmployeeCheckConfiguration(
        this.employeeID
      );
    }
  };

  public onCheckWork(): void {
    let category = CheckCategoryEnum.BREAK;

    if (this.indicatorLabel === `En pausa`) {
      category = CheckCategoryEnum.BREAK;
    } else if (
      this.indicatorLabel === `En curso` ||
      this.indicatorLabel === `Detenido`
    ) {
      category = CheckCategoryEnum.WORK;
    }

    this.category.setValue(category);
    this.prepareDataToSend();
    this.checkWidgetService.toggleCheck(this.employeeID, this.form.value);
  }

  public onCheckBreak(): void {
    let category = CheckCategoryEnum.WORK;

    if (this.indicatorLabel === `En pausa`) {
      category = CheckCategoryEnum.WORK;
    } else if (this.indicatorLabel === `En curso`) {
      category = CheckCategoryEnum.BREAK;
    }

    this.category.setValue(category);
    this.prepareDataToSend();
    this.checkWidgetService.toggleCheck(this.employeeID, this.form.value);
  }

  private getJourney = (journey: Journey): void => {
    if (!journey) {
      return;
    }

    this.journey = journey;
    this._cdr.markForCheck();
    this.manageMinutes(journey.isOpen);
    this.checkStatus();
    this.setCheckList();
  };

  private getJourneyTable = (journey: Journey): void => {
    if (!journey) {
      return;
    }

    this.journey = journey;
    this._cdr.markForCheck();

    this.setCheckList();
  };

  private getMinutes = (minutes: number): void => {
    if (!minutes) {
      return;
    }

    this.journey.totalJourney = minutes;
    this._cdr.markForCheck();
  };

  private getCheck = (check: Check): void => {
    if (!check) {
      return;
    }

    this.check = check;
    this._cdr.markForCheck();
  };

  private setCheckList(): void {
    this.infoTooltip = $localize`No hay fichajes en la jornada de hoy`;
    if (this.journey?.checkList?.length) {
      this.infoTooltip = ``;
      this.journey?.checkList?.forEach((check, index) => {
        this.infoTooltip = `
        ${this.infoTooltip}
        ${index + 1}) ${check}
        `;
      });
    }
    this._cdr.markForCheck();
  }

  private getCheckConfig = (checkConfig: EmployeeCheckSource[]) => {
    if (!(checkConfig && checkConfig[0])) {
      return;
    }

    this.enableCheck = checkConfig[0].enableWeb;
    this.allowBreakCheck = checkConfig[0].allowBreakChecks;
    this._cdr.markForCheck();
  };

  private manageMinutes(isOpen: boolean) {
    if (isOpen) {
      this.displayTimeService.start(this.journey.totalJourney);
    } else {
      this.displayTimeService.stop();
    }
  }

  private checkStatus() {
    const isOpen = this.journey?.isOpen;
    const category = this.journey?.currentCheckCategory || this.check?.category;

    if (this.allowBreakCheck) {
      if (isOpen) {
        if (category === CheckCategoryEnum.WORK) {
          this.buttonWork = CheckWidgetCardDesignConfig.active_work;
          this.buttonBreak = CheckWidgetCardDesignConfig.desactive_break;
          this.indicatorLabel = this.buttonWork.label;
          this.indicatorColor = this.buttonWork.classBgStatus;
        } else if (category === CheckCategoryEnum.BREAK) {
          this.buttonWork = CheckWidgetCardDesignConfig.active_work;
          this.buttonBreak = CheckWidgetCardDesignConfig.active_break;
          this.indicatorLabel = this.buttonBreak.label;
          this.indicatorColor = this.buttonBreak.classBgStatus;
        }
      } else {
        this.buttonWork = CheckWidgetCardDesignConfig.desactive_work;
        this.indicatorLabel = this.buttonWork.label;
        this.indicatorColor = this.buttonWork.classBgStatus;
        this.buttonBreak = null;
      }
    } else {
      this.buttonWork = this.journey?.isOpen
        ? CheckWidgetCardDesignConfig.active_work
        : CheckWidgetCardDesignConfig.desactive_work;

      this.indicatorLabel = this.buttonWork.label;
      this.indicatorColor = this.buttonWork.classBgStatus;
      this.buttonBreak = null;
    }
  }
}
